import { FC, useEffect } from 'react';
import {
	Button,
	CircularProgress,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControlLabel,
	Grid,
	OutlinedInput,
	Radio,
	RadioGroup,
	Stack,
} from '@mui/material';
import { z } from 'zod';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { FormField } from '../../form/FormField';
import { FormErrors } from '../../form/FormErrors';
import { useModalContext } from '../../modal/ModalContext';
import { graphql } from '../../gql';
import { useApiMutation, useApiQuery } from '../../query/hooks';
import { mutationResponseHandler } from '../../utils/mutationResponseHandler';
import { ControlledFormField } from '../../form/ControlledFormField';
import { DatePicker } from '../../form/DatePicker';
import { DateRangePicker } from '../../form/DateRangePicker';
import { MetricDataPoint } from '../../gql/graphql';
import { useTypedForm } from '../../../../shared/fe/hooks/useForm';
import dayjs from 'dayjs';
import { useAuthSignedIn } from '../../auth/useAuth';
import { useSnackbar } from '../../store/snackbar';

const getMetricDataPointQuery = graphql(`
	query editDataPoint_getMetricDataPoint($contentDataPointId: UUID!) {
		getContentMetricDataPoint(by: { id: $contentDataPointId }) {
			metricDataPoint {
				start
				end
				value
			}
		}
	}
`);

const getMetricQuery = graphql(`
	query editDataPoint_getMetric($contentMetricId: UUID!) {
		getContentMetric(by: { id: $contentMetricId }) {
			metric {
				dataSet {
					id
				}
			}
		}
	}
`);

const createMetricDataPointMutation = graphql(`
	mutation editDataPoint_createMetricDataPoint($start: DateTime, $end: DateTime, $value: Float, $dataSetId: UUID!, $creatorId: UUID!) {
		createContentMetricDataPoint(
			data: {
				creator: { connect: { id: $creatorId } }
				metricDataPoint: { create: { start: $start, end: $end, value: $value, dataSet: { connect: { id: $dataSetId } } } }
			}
		) {
			ok
			errorMessage
		}
	}
`);

const updateMetricDataPointMutation = graphql(`
	mutation editDataPoint_updateMetricDataPoint($dataPointId: UUID!, $start: DateTime, $end: DateTime, $value: Float) {
		updateContentMetricDataPoint(
			by: { id: $dataPointId }
			data: { metricDataPoint: { update: { start: $start, end: $end, value: $value } } }
		) {
			ok
			errorMessage
		}
	}
`);

const createFormSchema = (intl: IntlShape) =>
	z.object({
		value: z.preprocess(
			(numeric) => Number(`${numeric}`.replace(',', '.')),
			z.number().nonnegative(intl.formatMessage({ id: 'form.metric-data-point.error.invalid-value' })),
		),
		dateType: z.enum(['dateRange', 'startDate']),
		start: z
			.string()
			.datetime({
				offset: true,
				message: intl.formatMessage({ id: 'form.metric-data-point.error.invalid-end' }),
			})
			.nullable(),
		range: z
			.tuple([
				z
					.string()
					.datetime({ offset: true, message: intl.formatMessage({ id: 'form.metric-data-point.error.invalid-end' }) })
					.nullable(),
				z
					.string()
					.datetime({ offset: true, message: intl.formatMessage({ id: 'form.metric-data-point.error.invalid-end' }) })
					.nullable(),
			])
			.nullable(),
	});

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

const editDataPointModalSchema = z.union([
	z.object({
		contentMetricDataPointId: z.string(),
	}),
	z.object({
		contentMetricId: z.string(),
	}),
]);

type Props = z.infer<typeof editDataPointModalSchema>;

const EditDataPointModal: FC<Props> = (props) => {
	const contentMetricDataPointId = 'contentMetricDataPointId' in props ? props.contentMetricDataPointId : null;
	const contentMetricId = 'contentMetricId' in props ? props.contentMetricId : null;

	const intl = useIntl();
	const { me } = useAuthSignedIn();
	const { closeModal } = useModalContext();
	const { openSnackbar } = useSnackbar();

	const form = useTypedForm({
		schema: createFormSchema(intl),
		defaultValues: {
			value: '0',
			dateType: 'startDate',
			start: `${dayjs().format('YYYY-MM-DD')}T00:00:00Z`,
			range: [null, null],
		},
	});

	const dateType = form.watch('dateType');

	const { data: dataPointResult, isLoading: isDataPointLoading } = useApiQuery(getMetricDataPointQuery, {
		enabled: contentMetricDataPointId != null,
		variables: { contentDataPointId: contentMetricDataPointId ?? '' },
	});

	const { data: metricResult, isLoading: isMetricLoading } = useApiQuery(getMetricQuery, {
		enabled: contentMetricId != null,
		variables: { contentMetricId: contentMetricId ?? '' },
	});

	const isLoading = isDataPointLoading || isMetricLoading;

	// sync form initial values
	useEffect(() => {
		if (contentMetricDataPointId != null && dataPointResult?.getContentMetricDataPoint != null) {
			if (dataPointResult.getContentMetricDataPoint.metricDataPoint?.end == null) {
				form.reset({
					dateType: 'startDate',
					start: dataPointResult.getContentMetricDataPoint.metricDataPoint?.start,
					range: null,
					value: dataPointResult.getContentMetricDataPoint.metricDataPoint?.value.toString(),
				});
			} else {
				form.reset({
					dateType: 'dateRange',
					start: null,
					range: [
						dataPointResult.getContentMetricDataPoint.metricDataPoint.start,
						dataPointResult.getContentMetricDataPoint.metricDataPoint.end,
					],
					value: dataPointResult.getContentMetricDataPoint.metricDataPoint.value.toString(),
				});
			}
		}
	}, [form, contentMetricDataPointId, dataPointResult]);

	const { mutate: createMetricDataPoint } = useApiMutation(createMetricDataPointMutation, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.createContentMetricDataPoint.ok,
				toastMessages: {
					error: intl.formatMessage({ id: 'common.error.default' }),
				},
				errorMessage: result.createContentMetricDataPoint.errorMessage,
				openSnackbar,
			});
		},
	});

	const { mutate: updateMetricDataPoint } = useApiMutation(updateMetricDataPointMutation, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.updateContentMetricDataPoint.ok,
				toastMessages: {
					error: intl.formatMessage({ id: 'common.error.default' }),
				},
				errorMessage: result.updateContentMetricDataPoint.errorMessage,
				openSnackbar,
			});
		},
	});

	const convertFormValues = (values: FormValues): Pick<MetricDataPoint, 'start' | 'end' | 'value'> | null => {
		if (values.dateType === 'dateRange') {
			if (values.range == null || values.range[0] == null || values.range[1] == null) {
				form.setError('range', { message: intl.formatMessage({ id: 'form.metric-data-point.error.empty-range' }) });
				return null;
			}

			return {
				start: values.range[0],
				end: values.range[1],
				value: values.value,
			};
		}
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		if (values.dateType === 'startDate') {
			if (values.start == null) {
				form.setError('start', { message: intl.formatMessage({ id: 'form.metric-data-point.error.empty-start' }) });
				return null;
			}

			return {
				start: values.start,
				end: null,
				value: values.value,
			};
		}

		return null;
	};

	const onSubmit = (values: FormValues) => {
		const data = convertFormValues(values);
		if (data == null) {
			return;
		}

		if (contentMetricId != null) {
			createMetricDataPoint({
				...data,
				dataSetId: metricResult?.getContentMetric?.metric?.dataSet?.id ?? '',
				creatorId: me.id || '',
			});
		}

		if (contentMetricDataPointId != null) {
			updateMetricDataPoint({
				...data,
				dataPointId: contentMetricDataPointId,
			});
		}

		closeModal();
	};

	return (
		// @ts-ignore
		<form noValidate onSubmit={form.handleSubmit(onSubmit)}>
			<DialogTitle>
				{contentMetricDataPointId == null ? (
					<FormattedMessage id={'form.metric-data-point.create.title'} />
				) : (
					<FormattedMessage id={'form.metric-data-point.edit.title'} />
				)}
			</DialogTitle>
			<Divider />
			<DialogContent>
				{isLoading ? (
					<Stack p={3} alignItems={'center'}>
						<CircularProgress />
					</Stack>
				) : (
					<Grid container spacing={3}>
						<FormField
							form={form}
							name={'value'}
							label={intl.formatMessage({ id: 'form.metric-data-point.label.value' })}
							render={(props) => <OutlinedInput {...props} />}
						/>

						<ControlledFormField
							form={form}
							name={'dateType'}
							label={intl.formatMessage({ id: 'form.metric-data-point.label.date-type' })}
							render={(props) => (
								<RadioGroup row {...props}>
									<FormControlLabel
										value={'startDate'}
										control={<Radio />}
										label={intl.formatMessage({ id: 'form.metric-data-point.label.data-type.start-date' })}
									/>
									<FormControlLabel
										value={'dateRange'}
										control={<Radio />}
										label={intl.formatMessage({ id: 'form.metric-data-point.label.data-type.date-range' })}
									/>
								</RadioGroup>
							)}
						/>

						{dateType === 'startDate' && (
							<ControlledFormField
								form={form}
								name={'start'}
								label={intl.formatMessage({ id: 'form.metric-data-point.label.start' })}
								render={({ id, name, value, onChange }) => <DatePicker id={id} name={name} value={value} onChange={onChange} />}
							/>
						)}

						{dateType === 'dateRange' && (
							<ControlledFormField
								form={form}
								name={'range'}
								label={intl.formatMessage({ id: 'form.metric-data-point.label.date-range' })}
								render={({ id, name, value, onChange }) => <DateRangePicker id={id} name={name} value={value} onChange={onChange} />}
							/>
						)}

						<FormErrors form={form} />
					</Grid>
				)}
			</DialogContent>
			<DialogActions sx={{ p: 2.5 }}>
				<Stack direction={'row'} spacing={2} alignItems={'center'} width={'100%'} justifyContent={'end'}>
					<Button color={'secondary'} onClick={closeModal}>
						<FormattedMessage id={'common.cancel'} />
					</Button>
					<Button type={'submit'} variant={'contained'} disabled={form.formState.isSubmitting || !me.id}>
						{contentMetricDataPointId == null ? <FormattedMessage id={'common.create'} /> : <FormattedMessage id={'common.save'} />}
					</Button>
				</Stack>
			</DialogActions>
		</form>
	);
};

export { EditDataPointModal, editDataPointModalSchema };
